<template>
  <div class="media-page page-playlist-img h-full w-full p-4">

    <!-- Toolbar -->
    <section class="ind-toolbar mb-4">
      <div class="ind-toolbar-search">
        <i class="pi pi-search ind-search-icon"></i>
        <InputText
          v-model="searchQuery"
          placeholder="Buscar playlist..."
          class="ind-filter-input ind-filter-input-with-icon"
        />
      </div>
      <div class="ind-toolbar-types">
        <div class="ind-type-filters" role="group" aria-label="Filtrar por estado de imagen">
          <label
            v-for="opt in statusFilterOptions"
            :key="opt.value"
            class="pl-type-chip"
            :class="{ 'is-active': statusFilter === opt.value }"
          >
            <input
              type="radio"
              name="statusFilter"
              :checked="statusFilter === opt.value"
              @change="statusFilter = opt.value"
            />
            <i v-if="opt.icon" :class="opt.icon" style="font-size:0.72rem"></i>
            <span>{{ opt.label }}</span>
          </label>
        </div>
      </div>
      <div class="ind-toolbar-right">
        <Button
          label="Nueva playlist"
          icon="pi pi-plus"
          class="upload-action-btn"
          @click="openNewPlaylistDialog"
        />
        <Button
          icon="pi pi-refresh"
          class="p-button-text p-button-sm p-button-secondary"
          :loading="loading"
          @click="loadPlaylists"
          v-tooltip.left="'Recargar'"
        />
      </div>
    </section>

    <div class="ind-toolbar-count">
      <span class="text-xs text-zinc-400 dark:text-zinc-500">
        {{ filteredPlaylists.length }} playlists
        <span class="mx-1.5 opacity-50">·</span>
        <span class="text-red-400 dark:text-red-500">{{ playlistsSinImagen }} sin imagen</span>
        <span class="mx-1.5 opacity-50">·</span>
        <span class="text-green-500 dark:text-green-400">{{ playlistsConImagen }} con imagen</span>
      </span>
    </div>

    <!-- Error -->
    <div v-if="error" class="kpi-error-box mb-4">
      <i class="pi pi-exclamation-triangle mr-2"></i>{{ error }}
      <Button icon="pi pi-times" class="p-button-text p-button-sm p-button-secondary ml-auto" @click="error = ''" />
    </div>

    <!-- Tabla de playlists -->
    <section class="media-table-wrap">
      <DataTable
        :value="filteredPlaylists"
        :loading="loading"
        responsiveLayout="scroll"
        stripedRows
        class="text-sm playlist-table"
      >
        <Column field="id" header="ID" style="width:70px">
          <template #body="{ data }">
            <span class="font-mono text-xs">{{ data.id }}</span>
          </template>
        </Column>

        <Column header="Imagen" style="width:100px">
          <template #body="{ data }">
            <div class="playlist-img-cell">
              <div
                class="playlist-img-shell"
                :class="{ 'is-clickable': hasImage(data) }"
                @click="hasImage(data) && previewImage(data)"
              >
                <img
                  v-if="getImageUrl(data)"
                  :src="getImageUrl(data)"
                  :alt="data.name"
                  class="playlist-thumb"
                  @error="onImgError(data)"
                />
                <i v-else class="pi pi-image text-zinc-300 dark:text-zinc-600" style="font-size:1.2rem"></i>
              </div>
            </div>
          </template>
        </Column>

        <Column field="name" header="Nombre" style="min-width:200px">
          <template #body="{ data }">
            <span class="font-semibold text-zinc-700 dark:text-zinc-200">{{ data.name }}</span>
          </template>
        </Column>

        <Column field="category_name" header="Categoría" style="min-width:140px">
          <template #body="{ data }">
            <span class="text-xs px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300">
              {{ data.category_name || '—' }}
            </span>
          </template>
        </Column>

        <Column header="Estado" style="width:100px">
          <template #body="{ data }">
            <span
              class="playlist-status-dot"
              :class="hasImage(data) ? 'has-image' : 'no-image'"
            >
              <i :class="hasImage(data) ? 'pi pi-check-circle' : 'pi pi-minus-circle'"></i>
              <span>{{ hasImage(data) ? 'Con imagen' : 'Sin imagen' }}</span>
            </span>
          </template>
        </Column>

        <Column header="Acción" style="width:140px">
          <template #body="{ data }">
            <div class="flex gap-1.5 items-center">
              <Button
                icon="pi pi-pencil"
                class="p-button-sm p-button-text p-button-secondary btn-action-icon"
                v-tooltip.top="'Editar'"
                @click="openEditDialog(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </section>

    <!-- Dialog unificado -->
    <Dialog
      v-model:visible="dialogVisible"
      modal
      :header="dialogMode === 'new' ? 'Nueva playlist' : 'Editar: ' + (dialogTarget?.name || '')"
      :style="{ width: '540px' }"
      :breakpoints="{ '575px': '95vw' }"
      :closable="!saving"
      :dismissableMask="true"
    >
      <div class="flex flex-col gap-5 pt-2">

        <!-- Nombre -->
        <div class="flex flex-col gap-1.5">
          <label class="field-label text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Nombre</label>
          <InputText
            v-if="dialogMode === 'new'"
            v-model="form.name"
            placeholder="Nombre de la playlist"
            class="w-full"
            maxlength="255"
          />
          <InputText
            v-else
            :model-value="dialogTarget?.name"
            disabled
            class="w-full"
          />
        </div>

        <!-- Categoria -->
        <div class="flex flex-col gap-1.5">
          <label class="field-label text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Categoria</label>
          <Dropdown
            v-model="form.category_id"
            :options="categories"
            optionLabel="name"
            optionValue="id"
            placeholder="Selecciona una categoria"
            class="w-full"
          />
          <small v-if="dialogMode === 'new'" class="text-zinc-400 dark:text-zinc-500 text-xs">Por defecto se usara Especiales</small>
        </div>

        <!-- Imagen section -->
        <div class="flex flex-col gap-3">
          <label class="field-label text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Imagen</label>

          <!-- Comparison panel: edit mode + existing image -->
          <div v-if="dialogMode === 'edit' && dialogTarget && hasImage(dialogTarget)" class="img-compare-wrap">
            <!-- Old image -->
            <div class="img-compare-col">
              <span class="img-compare-label">Actual</span>
              <div class="img-frame img-frame-old">
                <img :src="getImageUrl(dialogTarget)" :alt="dialogTarget.name" class="img-frame-src" />
              </div>
            </div>

            <!-- Arrow -->
            <div class="img-compare-arrow">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-zinc-300 dark:text-zinc-600">
                <path d="M5 12h14M13 5l7 7-7 7"/>
              </svg>
            </div>

            <!-- New image slot -->
            <div class="img-compare-col">
              <span class="img-compare-label">Nueva</span>
              <div
                class="img-frame img-frame-new"
                :class="{ 'has-image': !!previewUrl, 'is-dragover': dragOver }"
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="onDropFile"
                @click="$refs.fileInput.click()"
              >
                <img v-if="previewUrl" :src="previewUrl" class="img-frame-src" />
                <div v-else class="img-frame-placeholder">
                  <i class="pi pi-plus text-lg text-zinc-300 dark:text-zinc-600"></i>
                  <span class="text-xs text-zinc-400 dark:text-zinc-500">Seleccionar</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Dropzone: new mode OR edit mode without existing image -->
          <div v-else
            class="img-dropzone"
            :class="{ 'is-dragover': dragOver, 'has-file': !!selectedFile }"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDropFile"
            @click="$refs.fileInput.click()"
          >
            <template v-if="!selectedFile">
              <i class="pi pi-image text-2xl text-zinc-300 dark:text-zinc-600"></i>
              <p class="text-sm text-zinc-400 dark:text-zinc-500 mt-1">Arrastra una imagen JPG o haz clic para seleccionar</p>
              <p class="text-xs text-zinc-300 dark:text-zinc-600 mt-1">600 x 600 px &middot; max. 1 MB</p>
            </template>
            <template v-else>
              <i class="pi pi-check-circle text-green-500 text-lg"></i>
              <p class="text-sm font-medium text-zinc-600 dark:text-zinc-300 ml-2">{{ selectedFile.name }}</p>
              <p class="text-xs text-zinc-400 dark:text-zinc-500 ml-auto">{{ formatFileSize(selectedFile.size) }}</p>
            </template>
          </div>

          <input
            ref="fileInput"
            type="file"
            accept="image/jpeg,image/jpg"
            style="display:none"
            @change="onFileSelected"
          />

          <!-- Selected file info + remove button (comparison mode) -->
          <div v-if="selectedFile && dialogMode === 'edit' && dialogTarget && hasImage(dialogTarget)" class="img-file-info">
            <div class="img-file-info-left">
              <i class="pi pi-file text-blue-500 text-xs"></i>
              <span class="text-sm text-zinc-600 dark:text-zinc-300 truncate">{{ selectedFile.name }}</span>
              <span class="text-xs text-zinc-400 dark:text-zinc-500">{{ formatFileSize(selectedFile.size) }}</span>
            </div>
            <button class="img-file-remove" @click="removeSelectedFile" title="Quitar archivo">
              <i class="pi pi-times"></i>
            </button>
          </div>
        </div>

        <!-- Progress bar -->
        <div v-if="saving" class="progress-bar-wrap">
          <div class="progress-bar-track">
            <div
              class="progress-bar-fill"
              :style="{ width: uploadProgress + '%' }"
            ></div>
          </div>
          <span class="progress-bar-text">{{ uploadProgress }}%</span>
        </div>
      </div>

      <template #footer>
        <div class="flex gap-2 w-full justify-end">
          <Button
            label="Cancelar"
            class="p-button-text"
            :disabled="saving"
            @click="closeDialog"
          />
          <Button
            :label="dialogMode === 'new' ? 'Crear playlist' : 'Guardar cambios'"
            icon="pi pi-check"
            :loading="saving"
            :disabled="!canSave"
            @click="save"
          />
        </div>
      </template>
    </Dialog>

    <!-- Preview dialog -->
    <Dialog
      v-model:visible="previewDialogVisible"
      modal
      :header="previewData?.name || 'Vista previa'"
      :style="{ width: 'auto' }"
      :dismissableMask="true"
    >
      <div class="flex justify-center p-2">
        <img
          v-if="previewData && getImageUrl(previewData)"
          :src="getImageUrl(previewData)"
          class="max-h-[70vh] rounded-lg shadow-lg"
        />
      </div>
    </Dialog>

    <Toast group="playlist-upload" position="bottom-right" />
  </div>
</template>

<script>
import axiosInstance from '@/axiosInstance';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const IMAGE_BASE = isLocal
  ? 'http://127.0.0.1:8000/category_images/'
  : 'https://media.vybroo.com/images/category/';

export default {
  name: 'PlaylistsImagenes',

  data() {
    return {
      playlists: [],
      categories: [],
      loading: false,
      error: '',
      searchQuery: '',
      statusFilter: 'all',
      statusFilterOptions: [
        { label: 'Todas', value: 'all', icon: '' },
        { label: 'Sin imagen', value: 'no-image', icon: 'pi pi-minus-circle' },
        { label: 'Con imagen', value: 'has-image', icon: 'pi pi-check-circle' },
      ],

      cacheBuster: 0,

      // Dialog unificado
      dialogVisible: false,
      dialogMode: 'edit', // 'new' | 'edit'
      dialogTarget: null,
      form: {
        name: '',
        category_id: null,
      },
      selectedFile: null,
      previewUrl: null,
      saving: false,
      uploadProgress: 0,
      dragOver: false,

      // Preview dialog
      previewDialogVisible: false,
      previewData: null,

      // Imagenes rotas
      brokenImages: new Set(),
    };
  },

  computed: {
    playlistsSinImagen() {
      return this.playlists.filter(p => !this.hasImage(p)).length;
    },

    playlistsConImagen() {
      return this.playlists.filter(p => this.hasImage(p)).length;
    },

    filteredPlaylists() {
      const q = this.searchQuery.trim().toLowerCase();
      let result = this.playlists;

      if (q) {
        result = result.filter(p =>
          p.name.toLowerCase().includes(q) ||
          String(p.id).includes(q)
        );
      }

      if (this.statusFilter === 'has-image') {
        result = result.filter(p => this.hasImage(p));
      } else if (this.statusFilter === 'no-image') {
        result = result.filter(p => !this.hasImage(p));
      }

      return result;
    },

    canSave() {
      if (this.saving) return false;
      if (this.dialogMode === 'new') {
        return this.form.name.trim().length > 0;
      }
      return true;
    },
  },

  mounted() {
    this.loadPlaylists();
    this.loadCategories();
  },

  methods: {
    async loadPlaylists() {
      this.loading = true;
      this.error = '';
      try {
        const res = await axiosInstance.post('/api/playlists_list', {
          token: localStorage.getItem('token'),
        });
        if (res.data.success || res.data.ok) {
          const raw = res.data.playlists || res.data.data?.playlists || [];
          this.playlists = raw;
        } else {
          this.error = res.data.message || res.data.msg || 'Error al cargar playlists';
        }
      } catch (e) {
        this.error = 'Error de conexion al cargar playlists';
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    loadCategories() {
      this.categories = [
        { id: 1, name: 'Listas de Éxitos' },
        { id: 2, name: 'Décadas' },
        { id: 3, name: 'Relax' },
        { id: 4, name: 'Ambient' },
        { id: 5, name: 'Sonidos del Mundo' },
        { id: 6, name: 'Regional Mexicano' },
        { id: 7, name: 'Fiesta Latina' },
        { id: 8, name: 'Workout/Cardio/Entrenamiento' },
        { id: 9, name: 'Blues & Friends/Blues' },
        { id: 10, name: 'Rock' },
        { id: 11, name: 'Infantil' },
        { id: 12, name: 'Indie' },
        { id: 13, name: 'Pop' },
        { id: 14, name: 'Disco y Funk' },
        { id: 15, name: 'Urbano' },
        { id: 16, name: 'Electrónica' },
        { id: 17, name: 'Jazz' },
        { id: 18, name: 'Instrumental' },
        { id: 19, name: 'Christmas' },
        { id: 20, name: 'Especiales' },
      ];
    },

    hasImage(data) {
      return !this.brokenImages.has(data.id);
    },

    getImageUrl(data) {
      if (!data || !data.id) return null;
      if (this.brokenImages.has(data.id)) return null;
      return IMAGE_BASE + data.id + '.jpg?t=' + this.cacheBuster;
    },

    onImgError(data) {
      if (data) this.brokenImages.add(data.id);
    },

    removeSelectedFile() {
      this.selectedFile = null;
      this.previewUrl = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    openNewPlaylistDialog() {
      this.dialogMode = 'new';
      this.dialogTarget = null;
      this.form = { name: '', category_id: 20 };
      this.selectedFile = null;
      this.previewUrl = null;
      this.uploadProgress = 0;
      this.dialogVisible = true;
    },

    openEditDialog(data) {
      this.dialogMode = 'edit';
      this.dialogTarget = data;
      this.form = { name: data.name, category_id: data.category_id || 20 };
      this.selectedFile = null;
      this.previewUrl = null;
      this.uploadProgress = 0;
      this.dialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
      this.dialogTarget = null;
      this.selectedFile = null;
      this.previewUrl = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    onFileSelected(e) {
      const file = e.target.files?.[0];
      if (file) this.validateAndSetFile(file);
    },

    onDropFile(e) {
      this.dragOver = false;
      const file = e.dataTransfer?.files?.[0];
      if (file) this.validateAndSetFile(file);
    },

    validateAndSetFile(file) {
      if (file.type !== 'image/jpeg' && file.type !== 'image/jpg') {
        this.$toast.add({
          severity: 'error', summary: 'Formato invalido',
          detail: 'Solo se permiten archivos JPG',
          life: 4000, group: 'playlist-upload',
        });
        return;
      }

      if (file.size > 1 * 1024 * 1024) {
        this.$toast.add({
          severity: 'error', summary: 'Archivo muy grande',
          detail: 'La imagen no debe superar 1 MB',
          life: 4000, group: 'playlist-upload',
        });
        return;
      }

      this.selectedFile = file;
      this.previewUrl = URL.createObjectURL(file);

      const img = new Image();
      img.onload = () => {
        if (img.width !== 600 || img.height !== 600) {
          this.$toast.add({
            severity: 'warn', summary: 'Dimension incorrecta',
            detail: `La imagen debe ser 600x600 (recibido: ${img.width}x${img.height})`,
            life: 6000, group: 'playlist-upload',
          });
        }
      };
      img.src = this.previewUrl;
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    async save() {
      if (!this.canSave) return;

      this.saving = true;
      this.uploadProgress = 0;

      try {
        const token = localStorage.getItem('token');

        if (this.dialogMode === 'new') {
          if (this.selectedFile) {
            const formData = new FormData();
            formData.append('token', token);
            formData.append('name', this.form.name.trim());
            formData.append('category_id', this.form.category_id || 20);
            formData.append('image', this.selectedFile);

            const res = await axiosInstance.post('/api/playlist_image_upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (e) => {
                if (e.total) {
                  this.uploadProgress = Math.round((e.loaded / e.total) * 100);
                }
              },
            });

            if (res.data.success || res.data.ok) {
              const pid = res.data.id || res.data.data?.id;
              if (pid) this.brokenImages.delete(pid);
              this.cacheBuster++;
              this.$toast.add({
                severity: 'success', summary: 'Playlist creada',
                detail: `"${res.data.name || res.data.data?.name || this.form.name}" con imagen`,
                life: 4000, group: 'playlist-upload',
              });
            } else {
              this.$toast.add({
                severity: 'error', summary: 'Error',
                detail: res.data.message || res.data.msg || 'Error al guardar',
                life: 5000, group: 'playlist-upload',
              });
              return;
            }
          } else {
            const res = await axiosInstance.post('/api/playlist_save', {
              token,
              name: this.form.name.trim(),
              category_id: this.form.category_id || 20,
            });

            if (!(res.data.success || res.data.ok)) {
              this.$toast.add({
                severity: 'error', summary: 'Error',
                detail: res.data.message || 'Error al crear playlist',
                life: 5000, group: 'playlist-upload',
              });
              return;
            }

            this.$toast.add({
              severity: 'success', summary: 'Playlist creada',
              detail: `"${this.form.name}" guardada (sin imagen)`,
              life: 4000, group: 'playlist-upload',
            });
          }

          this.cacheBuster++;
          this.closeDialog();
          await this.loadPlaylists();
          return;
        }

        // Modo edicion
        const { id, name } = this.dialogTarget;
        const categoryId = this.form.category_id || 20;

        const saveRes = await axiosInstance.post('/api/playlist_save', {
          token, id, name, category_id: categoryId,
        });

        if (!(saveRes.data.success || saveRes.data.ok)) {
          this.$toast.add({
            severity: 'error', summary: 'Error',
            detail: saveRes.data.message || 'Error al guardar categoria',
            life: 5000, group: 'playlist-upload',
          });
          return;
        }

        let imageUploaded = false;
        if (this.selectedFile) {
          const imgFormData = new FormData();
          imgFormData.append('token', token);
          imgFormData.append('id', id);
          imgFormData.append('image', this.selectedFile);

          const imgRes = await axiosInstance.post('/api/playlist_image_upload', imgFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
              if (e.total) {
                this.uploadProgress = Math.round((e.loaded / e.total) * 100);
              }
            },
          });

          if (imgRes.data.success || imgRes.data.ok) {
            this.brokenImages.delete(id);
            imageUploaded = true;
          } else {
            this.$toast.add({
              severity: 'warn', summary: 'Imagen no subida',
              detail: imgRes.data.message || 'Error al subir imagen',
              life: 5000, group: 'playlist-upload',
            });
          }
        }

        // Actualizar fila local sin recargar toda la tabla
        const idx = this.playlists.findIndex(p => p.id === id);
        const cat = this.categories.find(c => c.id === categoryId);
        const catName = cat ? cat.name : '';
        if (idx !== -1) {
          this.playlists[idx].category_id = categoryId;
          this.playlists[idx].category_name = catName;
        }

        this.cacheBuster++;

        this.closeDialog();

        // Recargar para refrescar las imagenes en la tabla
        await this.loadPlaylists();

        this.$toast.add({
          severity: 'success', summary: 'Playlist actualizada',
          detail: `"${name}" a ${catName}${imageUploaded ? ' + imagen' : ''}`,
          life: 3000, group: 'playlist-upload',
        });

      } catch (e) {
        console.error('Error saving playlist:', e);
        this.$toast.add({
          severity: 'error', summary: 'Error',
          detail: e.response?.data?.message || e.message || 'Error de conexion',
          life: 5000, group: 'playlist-upload',
        });
      } finally {
        this.saving = false;
      }
    },

    previewImage(data) {
      this.previewData = data;
      this.previewDialogVisible = true;
    },
  },
};
</script>

<style scoped>
@import "@/assets/media-module-base.css";

.page-playlist-img {
  --media-hero-bg: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
  --media-hero-border: rgba(244, 114, 182, 0.35);
  --media-hero-bg-dark: linear-gradient(135deg, rgba(39, 20, 30, 0.95) 0%, rgba(24, 24, 27, 0.98) 100%);
  --media-hero-border-dark: rgba(244, 114, 182, 0.2);
}

/* ==================== TOOLBAR ==================== */

.page-playlist-img .ind-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.page-playlist-img .ind-toolbar-search {
  position: relative;
  flex: 0 0 320px;
  min-width: 240px;
  max-width: 360px;
}

.page-playlist-img .ind-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.76rem;
  color: #94a3b8;
  pointer-events: none;
  z-index: 1;
}

.page-playlist-img .ind-filter-input {
  width: 100%;
  height: 42px;
  border-radius: 10px;
}

.page-playlist-img .ind-filter-input-with-icon.p-inputtext {
  padding-left: 2rem;
}

.page-playlist-img .ind-toolbar-types {
  flex: 1 1 0;
  min-width: 360px;
  display: flex;
  justify-content: center;
}

.page-playlist-img .ind-type-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-height: 42px;
  padding-top: 2px;
  justify-content: center;
  width: 100%;
}

.pl-type-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 999px;
  padding: 4px 9px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #475569;
  background: rgba(255, 255, 255, 0.94);
  cursor: pointer;
  user-select: none;
  transition: border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.pl-type-chip input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.pl-type-chip:hover {
  transform: translateY(-1px);
}

.pl-type-chip.is-active {
  box-shadow: inset 0 0 0 1px rgba(147, 197, 253, 0.4);
}

.pl-type-chip:nth-of-type(1).is-active {
  border-color: rgba(59, 130, 246, 0.6);
  background: rgba(219, 234, 254, 0.9);
  color: #1d4ed8;
}

.pl-type-chip:nth-of-type(2).is-active {
  border-color: rgba(239, 68, 68, 0.55);
  background: rgba(254, 226, 226, 0.95);
  color: #b91c1c;
}

.pl-type-chip:nth-of-type(3).is-active {
  border-color: rgba(34, 197, 94, 0.6);
  background: rgba(220, 252, 231, 0.95);
  color: #15803d;
}

.dark .pl-type-chip {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #d4d4d8;
}

.dark .pl-type-chip:hover {
  border-color: rgba(96, 165, 250, 0.55);
  background: rgba(30, 41, 59, 0.85);
  color: #bfdbfe;
}

.dark .pl-type-chip:nth-of-type(1).is-active {
  border-color: rgba(96, 165, 250, 0.75);
  background: rgba(30, 58, 138, 0.3);
  color: #dbeafe;
}

.dark .pl-type-chip:nth-of-type(2).is-active {
  border-color: rgba(248, 113, 113, 0.7);
  background: rgba(127, 29, 29, 0.28);
  color: #fecaca;
}

.dark .pl-type-chip:nth-of-type(3).is-active {
  border-color: rgba(74, 222, 128, 0.75);
  background: rgba(20, 83, 45, 0.28);
  color: #dcfce7;
}

.page-playlist-img .ind-toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.page-playlist-img .ind-toolbar-count {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 4px 0 10px;
  line-height: 1;
  opacity: 0.72;
  font-size: 0.74rem;
  color: #94a3b8;
}

.dark .page-playlist-img .ind-toolbar-count {
  color: #71717a;
}

/* ==================== ERROR BOX ==================== */

.kpi-error-box {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid rgba(239, 68, 68, 0.4);
  background: rgba(254, 226, 226, 0.5);
  color: #b91c1c;
  font-size: 0.9rem;
}

.dark .kpi-error-box {
  background: rgba(69, 10, 10, 0.4);
  color: #fca5a5;
  border-color: rgba(239, 68, 68, 0.3);
}

/* ==================== TABLE / IMAGE CELL ==================== */

.playlist-img-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.playlist-img-shell {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(241, 245, 249, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.playlist-img-shell.is-clickable {
  cursor: pointer;
}

.playlist-img-shell.is-clickable:hover {
  transform: scale(1.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.playlist-img-shell.is-clickable:active {
  transform: scale(1.03);
}

.dark .playlist-img-shell {
  border-color: rgba(82, 82, 91, 0.4);
  background: rgba(24, 24, 27, 0.6);
}

.playlist-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.playlist-status-dot {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.playlist-status-dot.has-image {
  color: #16a34a;
}

.playlist-status-dot.no-image {
  color: #a1a1aa;
}

.dark .playlist-status-dot.has-image {
  color: #4ade80;
}

.dark .playlist-status-dot.no-image {
  color: #52525b;
}

/* ==================== DIALOG: IMAGE COMPARISON ==================== */

.img-compare-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0;
}

.img-compare-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 120px;
}

.img-compare-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
}

.dark .img-compare-label {
  color: #71717a;
}

.img-compare-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: center;
  height: 120px;
}

/* Image frames */
.img-frame {
  width: 120px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.img-frame-src {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Old image: slightly faded */
.img-frame-old {
  border: 2px solid rgba(148, 163, 184, 0.3);
  background: rgba(241, 245, 249, 0.5);
  opacity: 0.72;
  filter: saturate(0.85);
}

.dark .img-frame-old {
  border-color: rgba(82, 82, 91, 0.4);
  background: rgba(24, 24, 27, 0.4);
  opacity: 0.85;
}

/* New image slot: dashed border + hover effect */
.img-frame-new {
  border: 2px dashed rgba(148, 163, 184, 0.45);
  background: rgba(248, 250, 252, 0.6);
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, opacity 0.2s ease;
}

.dark .img-frame-new {
  border-color: rgba(82, 82, 91, 0.5);
  background: rgba(24, 24, 27, 0.35);
}

.img-frame-new:hover {
  border-color: rgba(59, 130, 246, 0.55);
  background: rgba(239, 246, 255, 0.7);
}

.dark .img-frame-new:hover {
  border-color: rgba(96, 165, 250, 0.5);
  background: rgba(30, 58, 138, 0.2);
}

.img-frame-new.is-dragover {
  border-color: rgba(59, 130, 246, 0.7);
  background: rgba(219, 234, 254, 0.7);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.dark .img-frame-new.is-dragover {
  border-color: rgba(96, 165, 250, 0.7);
  background: rgba(30, 58, 138, 0.3);
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
}

/* Once a file is selected, show solid border */
.img-frame-new.has-image {
  border-style: solid;
  border-color: rgba(34, 197, 94, 0.5);
  opacity: 1;
}

.dark .img-frame-new.has-image {
  border-color: rgba(74, 222, 128, 0.4);
}

.img-frame-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 100%;
}

/* Selected file info bar (below comparison) */
.img-file-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(241, 245, 249, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.dark .img-file-info {
  background: rgba(24, 24, 27, 0.4);
  border-color: rgba(82, 82, 91, 0.3);
}

.img-file-info-left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.img-file-remove {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #a1a1aa;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  transition: background-color 0.15s ease, color 0.15s ease;
  flex-shrink: 0;
}

.img-file-remove:hover {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

/* ==================== DIALOG: DROPZONE ==================== */

.img-dropzone {
  border: 2px dashed rgba(148, 163, 184, 0.5);
  border-radius: 12px;
  padding: 24px 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  background: rgba(248, 250, 252, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.dark .img-dropzone {
  border-color: rgba(82, 82, 91, 0.5);
  background: rgba(24, 24, 27, 0.5);
}

.img-dropzone:hover,
.img-dropzone.is-dragover {
  border-color: rgba(59, 130, 246, 0.6);
  background: rgba(239, 246, 255, 0.8);
}

.dark .img-dropzone:hover,
.dark .img-dropzone.is-dragover {
  border-color: rgba(96, 165, 250, 0.5);
  background: rgba(30, 58, 138, 0.25);
}

.img-dropzone.has-file {
  flex-direction: row;
  border-color: rgba(34, 197, 94, 0.5);
  background: rgba(240, 253, 244, 0.7);
  padding: 12px 16px;
}

.dark .img-dropzone.has-file {
  border-color: rgba(74, 222, 128, 0.4);
  background: rgba(22, 101, 52, 0.2);
}

/* ==================== PROGRESS BAR ==================== */

.progress-bar-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-bar-track {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.2);
  overflow: hidden;
}

.dark .progress-bar-track {
  background: rgba(82, 82, 91, 0.3);
}

.progress-bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  transition: width 0.35s ease;
}

.progress-bar-text {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  min-width: 32px;
  text-align: right;
}

.dark .progress-bar-text {
  color: #71717a;
}

/* ==================== MISC ==================== */

.btn-action-icon.p-button {
  width: 28px;
  height: 28px;
}

.playlist-table .p-datatable-thead > tr > th {
  padding: 0.42rem 0.5rem;
  font-size: 0.76rem;
}

.playlist-table .p-datatable-tbody > tr > td {
  padding: 0.36rem 0.5rem;
}

.page-playlist-img .upload-action-btn {
  height: 42px;
  min-width: 144px;
  border-radius: 10px;
}
</style>
